import React from 'react'
import { Prompt } from 'react-router'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import PermissionErrorPage from '../../PermissionError'
import AgentPage from '@SonarCX/components/templates/AgentPage'
import PageContent from '@SonarCX/components/molecules/PageContent'
import LoadingSpinner from '@SonarCX/components/atoms/LoadingSpinner'
import EngagementHubV2 from '@SonarCX/components/organisms/EngagementHubV2'
import ChatWindowV2 from '@SonarCX/components/organisms/ChatWindowV2'
import MergeContactModal from '@SonarCX/components/molecules/MergeContactModal'

import useAccess from '@SonarCX/hooks/useAccess'
import { canPullTimeline, CAN_PULL_RESULT } from '../../ClientChats/helpers'
import { selectAccess } from '@SonarCX/slices/accessSlice'
import { selectConvo, loadAllConversations } from '@SonarCX/slices/convo'
import { makePrivate, selectChatBox } from '@SonarCX/slices/chatBoxSlice'
import { selectBaseInbox } from '@SonarCX/slices/baseInbox'
import { reset } from '@SonarCX/slices/chatControlsSlice'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import { useAddRemoveParticipant } from '@SonarCX/providers/AddRemoveParticipantProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useMenuVisibility } from '@SonarCX/providers/MenuVisibilityProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { useReadStatus } from '@SonarCX/providers/ReadStatusProvider'
import useStyles from '../sharedStyles'

const SessionTimeline = ({ session }) => {
  const dispatch = useAppDispatch()

  const { loaded: accessLoaded } = useAppSelector(selectAccess)
  const { allConversationsLoaded } = useAppSelector(selectConvo)
  const { currentID } = useAppSelector(selectChatBox)
  const { loaded: inboxesLoaded } = useAppSelector(selectBaseInbox)

  const {
    timeline: { notFound: timelineNotFound, setNotFound: setTimelineNotFound },
    api: {
      fetchTimeline,
      fetchSingleSession,
      finishFetch,
      acceptConversation,
      endSessions,
      rejectConversation
    }
  } = useSocket()
  const {
    id: selectedConversation,
    conversation,
    handleSelect: handleConvoSelect,
    conversations
  } = useQueueState()
  const { setCurrentUserIDs } = useAddRemoveParticipant()
  const hasPermission = useAccess()
  const { user: currentUser, loaded: envLoaded } = useEnvironment()
  const { show: showMenu, hide: hideMenu } = useMenuVisibility()
  const { down767: onMobile } = useBreakpoint()
  const { allUsers, loadUser } = useUserAPI()
  const { setOwner } = useOwner()
  const { saveSessions } = useReadStatus()

  // Conversations are loaded from socket
  const [fetching, setFetching] = React.useState([])
  const [selectedFirst, setSelectedFirst] = React.useState(false)
  const [wasAdded, setWasAdded] = React.useState(false)
  const [showMergeModal, setShowMergeModal] = React.useState(false)
  const ownerID = React.useMemo(
    () => conversation?.owner_id ?? '',
    [conversation]
  )

  const classes = useStyles()

  React.useEffect(() => {
    if (timelineNotFound) {
      setTimelineNotFound(false)
    }
    //eslint-disable-next-line
  }, [])

  // Fetches conversation on first selecting
  React.useEffect(() => {
    if (!accessLoaded || !envLoaded) {
      return
    }

    if (!conversation) {
      return
    }

    if (fetching.includes(selectedConversation)) {
      return
    }

    if (!conversation.fetched) {
      setFetching(old => [...new Set([...old, selectedID])])
      const onComplete = () =>
        setFetching(old => old.filter(id => id !== selectedID))

      const selectedID = selectedConversation
      const result = canPullTimeline(hasPermission, conversation, currentUser)
      if ([CAN_PULL_RESULT.FULL, CAN_PULL_RESULT.SESSION].includes(result)) {
        if (result === CAN_PULL_RESULT.FULL) {
          fetchTimeline(selectedID, false).then(onComplete)
        } else {
          fetchSingleSession(selectedID).then(onComplete)
        }
      } else {
        finishFetch(selectedID, false)
        onComplete()
      }
    }
  }, [
    selectedConversation,
    conversation,
    fetching,
    fetchTimeline,
    fetchSingleSession,
    finishFetch,
    accessLoaded,
    envLoaded,
    currentUser,
    hasPermission
  ])

  // Selects the newest conversation after loading
  React.useEffect(() => {
    if (!allConversationsLoaded || selectedFirst) {
      return
    }

    sessionStorage.setItem('viewing', session)

    // Select convsersation if you've joined it, otherwise join it
    if (!(session in conversations)) {
      acceptConversation(session, false)
      setWasAdded(true)
    }
    handleConvoSelect(session)
    setSelectedFirst(true)
  }, [
    allConversationsLoaded,
    selectedFirst,
    session,
    conversations,
    acceptConversation,
    handleConvoSelect
  ])

  // setting current users in convo to provider
  React.useEffect(() => {
    if (!conversation) {
      return
    }

    setCurrentUserIDs(conversation.users?.filter(id => id !== currentUser?.id))
  }, [conversation, currentUser, setCurrentUserIDs])

  // Reset owner if id changes
  React.useEffect(() => {
    setOwner(null)
  }, [ownerID, setOwner])

  // Load user if needed
  React.useEffect(() => {
    if (ownerID) {
      loadUser(ownerID)
    }
  }, [loadUser, ownerID])

  // Update owner on users change
  React.useEffect(() => {
    const user = allUsers[ownerID]

    if (user) {
      setOwner(user)
    }
  }, [allUsers, ownerID, setOwner])

  // Hide top menu
  React.useEffect(() => {
    onMobile ? hideMenu() : showMenu()
  }, [onMobile, hideMenu, showMenu])

  const openMergeModal = React.useCallback(() => {
    setShowMergeModal(true)
  }, [])

  const hideMergeModal = React.useCallback(() => {
    setShowMergeModal(false)
  }, [])

  // Closes the current session
  const handleEndConversation = React.useCallback(() => {
    endSessions([selectedConversation])
  }, [endSessions, selectedConversation])

  // Switch to private mode
  React.useEffect(() => {
    if (currentID) {
      dispatch(makePrivate())
    }
  }, [currentID, dispatch])

  // Loads conversations after inboxes, since they may not be loaded at this point yet
  React.useEffect(() => {
    if (inboxesLoaded && !allConversationsLoaded) {
      dispatch(
        loadAllConversations(store => {
          saveSessions(store)
        })
      )
    }
  }, [inboxesLoaded, allConversationsLoaded, dispatch, saveSessions])

  if (timelineNotFound === true) {
    return <PermissionErrorPage />
  }

  return (
    <>
      <AgentPage disablePadding>
        <div className={classes.root}>
          <PageContent toggleView={() => {}}>
            {!conversation ? (
              <LoadingSpinner />
            ) : (
              <div className={classes.container}>
                <EngagementHubV2 openMergeModal={openMergeModal} isOnLeft />

                <ChatWindowV2
                  endSession={handleEndConversation}
                  isDirect={false}
                  onlyShowDateSeparators
                />
              </div>
            )}
          </PageContent>
        </div>

        {showMergeModal && <MergeContactModal onClose={hideMergeModal} />}
      </AgentPage>

      <Prompt
        message={() => {
          if (session) {
            // Only remove the conversation if it was added due to trying to view it
            if (wasAdded) {
              rejectConversation(session, false)
            }
          }
          showMenu()
          dispatch(reset())
          return true
        }}
        when
      />
    </>
  )
}

export default SessionTimeline
